'use client';
import React from 'react';
import { Menu, Bell, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useNotifications } from '@/lib/hooks/useNotifications';

interface DashboardTopbarProps {
  role: 'customer' | 'cleaner' | 'admin';
  onMenuToggle: () => void;
  onSidebarToggle: () => void;
  sidebarCollapsed: boolean;
}

const roleAvatars = {
  customer: { name: 'Sarah Mitchell', initials: 'SM', color: 'bg-primary/10 text-primary' },
  cleaner: { name: 'Marcus Johnson', initials: 'MJ', color: 'bg-success-bg text-success' },
  admin: { name: 'Admin User', initials: 'AU', color: 'bg-warning-bg text-warning' },
};

const notifHrefMap: Record<string, string> = {
  customer: '/notifications',
  cleaner: '/cleaner-notifications',
  admin: '/admin-notifications',
};

export default function DashboardTopbar({ role, onMenuToggle, onSidebarToggle, sidebarCollapsed }: DashboardTopbarProps) {
  const user = roleAvatars[role];
  const { unreadCount } = useNotifications(role);
  const notifHref = notifHrefMap[role] ?? '/notifications';

  return (
    <header className="h-14 bg-card border-b border-border flex items-center px-4 gap-3 flex-shrink-0">
      {/* Mobile menu */}
      <button
        onClick={onMenuToggle}
        className="lg:hidden p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
        aria-label="Open navigation"
      >
        <Menu size={20} />
      </button>
      {/* Desktop sidebar toggle */}
      <button
        onClick={onSidebarToggle}
        className="hidden lg:flex p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
        aria-label="Toggle sidebar"
      >
        {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </button>

      {/* Search */}
      <div className="flex-1 max-w-sm hidden md:flex items-center gap-2 bg-muted rounded-xl px-3 py-2">
        <Search size={16} className="text-muted-foreground" />
        <input
          type="text"
          placeholder="Search jobs, customers..."
          className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none flex-1"
        />
      </div>

      <div className="flex-1 lg:flex-none" />

      {/* Notifications bell with live badge */}
      <Link
        href={notifHref}
        className="relative p-2 rounded-xl text-muted-foreground hover:bg-muted transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Link>

      {/* Avatar */}
      <div className="flex items-center gap-2 pl-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${user.color}`}>
          {user.initials}
        </div>
        <span className="hidden md:block text-sm font-medium text-foreground">{user.name}</span>
      </div>
    </header>
  );
}